import { useSyncExternalStore } from 'react'
import { Compass, Mic2, Music2 } from 'lucide-react'
import type { ArtistOrder, DiscoverOrder, SongOrder } from './radioArtistsModel'
import type { TopWindowKey } from './streamOrderMapping'

// Music's tab (Discover / Artists / My Music) and the Discover and Artists orders live in the panel's header
// card (SearchHeader) but drive the body (MusicStream, MusicDiscover, RadioArtists), so the two
// share this small per-panel store.

// 'radio' is the station's own stream, which only search shows now.
export type MusicTab = 'artists' | 'discover' | 'mine' | 'radio'

export const MUSIC_TABS = [
    { key: 'discover', label: 'Discover', icon: Compass, testId: 'music-tab-discover' },
    { key: 'artists', label: 'Artists', icon: Mic2, testId: 'music-tab-artists' },
]

// The viewer's own songs: signed in only.
export const MY_MUSIC_TAB = {
    key: 'mine',
    label: 'My Music',
    icon: Music2,
    testId: 'music-tab-mine',
}

export const musicTabsFor = (signedIn: boolean) =>
    signedIn ? [...MUSIC_TABS, MY_MUSIC_TAB] : MUSIC_TABS

export type MusicPanelView = {
    tab: MusicTab
    order: ArtistOrder
    songOrder: SongOrder
    discoverOrder: DiscoverOrder
    // Top's window (null = all time); only read while discoverOrder is 'top'.
    discoverWindow: TopWindowKey | null
}

const DEFAULT_VIEW: MusicPanelView = {
    tab: 'discover',
    order: 'hot',
    songOrder: 'plays',
    discoverOrder: 'time',
    discoverWindow: null,
}
const views = new Map<string, MusicPanelView>()
const listeners = new Set<() => void>()

export function getMusicPanelView(panelId: string | null | undefined): MusicPanelView {
    return (panelId && views.get(panelId)) || DEFAULT_VIEW
}

export function setMusicPanelView(
    panelId: string | null | undefined,
    part: Partial<MusicPanelView>,
) {
    if (!panelId) return
    const prev = getMusicPanelView(panelId)
    const next = { ...prev, ...part }
    if (
        next.tab === prev.tab &&
        next.order === prev.order &&
        next.songOrder === prev.songOrder &&
        next.discoverOrder === prev.discoverOrder &&
        next.discoverWindow === prev.discoverWindow
    )
        return
    views.set(panelId, next)
    listeners.forEach(listener => listener())
}

const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

export function useMusicPanelView(panelId: string | null | undefined): MusicPanelView {
    return useSyncExternalStore(
        subscribe,
        () => getMusicPanelView(panelId),
        () => DEFAULT_VIEW,
    )
}
